import { ApolloClient, useApolloClient } from "@apollo/client";
import { usePatientActionMutation } from "apollo/hooks/mutations";
import {
  PATIENT_ACTION,
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
} from "core/consts";
import { getErrorMessage } from "core/model/utils/errors";
import {
  ClinicalConsultation,
  EncryptionContext,
  MedicalDiagnosis,
  Patient,
  PatientActionType,
  PatientProfile,
  PatientWithClinicalConsultations,
  QueryProgress,
} from "core/types";
import { useCallback, useState } from "react";

export type ActionPayloads = {
  [PATIENT_ACTION.CREATE_CLINICAL_CONSULTATION]: {
    patient_id: number;
    context: {
      patient: {
        id: number;
        profile?: Pick<
          PatientProfile,
          "first_name" | "last_name" | "birth_date" | "gender"
        >;
        diagnosis?: Pick<
          MedicalDiagnosis,
          "hl7_main_diagnoses" | "main_diagnosis"
        >;
        seald_encryption_context?: EncryptionContext;
        clinical_consultations: Array<Pick<ClinicalConsultation, "patient_id">>;
      };
    };
  };
};

type ActionPayload<A extends PatientActionType> = A extends keyof ActionPayloads
  ? ActionPayloads[A]
  : never;

type ActionResults = {
  [PATIENT_ACTION.CREATE_CLINICAL_CONSULTATION]: PatientWithClinicalConsultations;
};

type ActionResult<A extends PatientActionType> = A extends keyof ActionResults
  ? ActionResults[A]
  : Patient;

export type PatientActionFunction<A extends PatientActionType> = (
  args: CallPatientActionProps<A>,
) => Promise<ActionResult<A> | null>;

type UsePatientActionReturnType<A extends PatientActionType> = [
  PatientActionFunction<A>,
  QueryProgress,
  () => void,
];

type CallPatientActionProps<A extends PatientActionType> = {
  patient: Patient;
  context: ActionPayload<A>["context"];
  onCompleted?: (
    result: ActionResult<A> | null,
    client: ApolloClient<object>,
  ) => void;
  onError?: (error: unknown) => void;
};

export const usePatientAction = <A extends PatientActionType>({
  actionType,
  refetchQueries,
}: {
  actionType: A;
  refetchQueries?: Parameters<
    typeof usePatientActionMutation
  >[0]["refetchQueries"];
}): UsePatientActionReturnType<A> => {
  const client = useApolloClient();
  const [patientAction, , resetProgress] = usePatientActionMutation({
    actionType,
    refetchQueries,
  });

  const [queryProgress, setQueryProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );

  const callAction = useCallback(
    async ({
      context,
      onCompleted,
      onError,
      patient,
    }: CallPatientActionProps<A>): Promise<ActionResult<A> | null> => {
      setQueryProgress(QUERY_PROGRESS_PENDING);
      if (!patient?.id) {
        const error = `Patient passed to usePatientAction is missing for actionType ${actionType}`;
        console.error(error);
        setQueryProgress(QUERY_PROGRESS_FAILED);
        throw new Error(error);
      }

      const payload: ActionPayload<A> = {
        context,
        patient_id: patient.id,
      } as ActionPayload<A>;

      try {
        const { data } = await patientAction(payload, {
          patientId: patient.id,
        });

        const result = data?.patient ?? null;

        if (!result) throw new Error("No patient returned");

        const typedResult = result as ActionResult<A>;
        if (onCompleted) onCompleted(typedResult, client);
        setQueryProgress(QUERY_PROGRESS_SUCCEED);
        return typedResult;
      } catch (err) {
        setQueryProgress(QUERY_PROGRESS_FAILED);
        console.error(`${actionType} - `, getErrorMessage(err));
        if (onError) onError(err);
        return null;
      }
    },
    [actionType, patientAction, client],
  );

  const reset = useCallback(() => {
    setQueryProgress(QUERY_PROGRESS_NOT_STARTED);
    resetProgress();
  }, [resetProgress]);

  return [callAction, queryProgress, reset];
};
